<template>
  <div class="adventure-summary-settings">
    <div class="adventure-summary-settings__row">
      <GlobalAccentColorPicker :dropdownPosition="dropdownPosition" />
    </div>

    <div class="adventure-summary-settings__row">
      <SrpSwitch
        :isChecked="showAdventureSummaryHeaderImage"
        :isDisabled="isLoading || isSavingHeaderToggle"
        @change="onHeaderImageChange"
      >
        <b>Show header image</b>
      </SrpSwitch>
      <InfoIconWithTooltip class="adventure-summary-settings__info-icon">Whether to show the primary image as the first element of the summary</InfoIconWithTooltip>
    </div>
  </div>
</template>

<!-- prettier-ignore -->
<script lang="ts">export default { name: "AdventureSummarySettings" };</script>

<script setup lang="ts">
import axios from "axios";
import { onMounted, ref } from "vue";
import { useRoute } from "vue-router";

import GlobalAccentColorPicker from "@components/GlobalAccentColorPicker.vue";
import InfoIconWithTooltip from "@components/ui/InfoIconWithTooltip.vue";
import SrpSwitch from "@components/ui/SrpSwitch.vue";
import type { WidgetSettingsForAdmin } from "@contracts/widgetsSettings";

withDefaults(
  defineProps<{
    dropdownPosition?: "bottom" | "top";
  }>(),
  {
    dropdownPosition: "bottom",
  }
);

const route = useRoute();

const showAdventureSummaryHeaderImage = ref(true);
const isLoading = ref(false);
const isSavingHeaderToggle = ref(false);

async function loadSettings(): Promise<void> {
  isLoading.value = true;
  try {
    const { data } = await axios.get<WidgetSettingsForAdmin>(`${import.meta.env.VITE_API_URL}/widget-admin/${route.params.pageId}/widgets/settings`);
    showAdventureSummaryHeaderImage.value = data.widgetSettings.showAdventureSummaryHeaderImage !== false;
  } finally {
    isLoading.value = false;
  }
}

onMounted(async () => {
  await loadSettings();
});

async function onHeaderImageChange(event: Event): Promise<void> {
  const checked = (event.target as HTMLInputElement).checked;
  const previous = showAdventureSummaryHeaderImage.value;
  showAdventureSummaryHeaderImage.value = checked;
  isSavingHeaderToggle.value = true;
  try {
    await axios.put(`${import.meta.env.VITE_API_URL}/widget-admin/${route.params.pageId}/widgets/settings?showAdventureSummaryHeaderImage=${checked}`);
  } catch (e) {
    showAdventureSummaryHeaderImage.value = previous;
    console.error(e);
  } finally {
    isSavingHeaderToggle.value = false;
  }
}
</script>

<style scoped lang="scss">
.adventure-summary-settings {
  color: #5b5b5b;
  font: 14px/18px sans-serif;

  &__row {
    display: flex;
    align-items: center;
    min-height: 36px;

    & + & {
      margin-top: 14px;
    }
  }

  &__info-icon {
    margin-left: 8px;
    position: relative;
    z-index: 1;
  }
}
</style>
